import { AxiosError, AxiosResponse, HttpStatusCode } from "axios";
import { useState } from "react";
import { Maybe } from "yup";

import { apiCall, APICoreProps } from "../lib/core";
import { ApiError, ErrorResponse } from "../types/common.types";

/**
 * Types
 */
// Common
export type APIOnCompleted<Response> = (res: AxiosResponse<Response>) => void;
export type APIOnError = (e: any) => void;
export type APIOptions<Response> = {
  onCompleted?: APIOnCompleted<Response>;
  onError?: APIOnError;
  disableToastErrorOn?: HttpStatusCode[];
};

// Base
export type APIBaseCall<Response, Request = object> = (
  myProps?: APICoreProps<Request>
) => Promise<Maybe<AxiosResponse<Maybe<Response>>>>;
export type UseAPIBaseProps<Response, Request = object> = APICoreProps<Request> & {
  options: APIOptions<Response>;
};

/**
 * Hook
 */
export function useApiBase<Response, Request = object>(props: UseAPIBaseProps<Response, Request>) {
  /**
   * props
   */
  const { body, header, kind, options, url } = props;

  /**
   * Hooks
   */
  // const { toast } = useToasts();

  /**
   * State
   */
  const [loading, setLoading] = useState(false);
  const [called, setCalled] = useState(false);

  /**
   * Callback
   */
  // 🟤 Cb - MAIN async call
  const call: APIBaseCall<Maybe<Response>, Request> = (myProps?: APICoreProps<Request>) => {
    // Init
    setLoading(true);
    const props: APICoreProps<Request> = { url, kind, header, body };
    const selectedHeader = { ...header, ...myProps?.header };
    const selectedProps = { ...(myProps ?? props), header: selectedHeader };
    // Call
    const asyncCall = new Promise<Maybe<AxiosResponse<Maybe<Response>>>>((resolve, reject) => {
      apiCall<Response, Request>(selectedProps)
        .then((res) => {
          resolve(res);
          options.onCompleted?.(res);
        })
        .catch((e: AxiosError<ApiError | ErrorResponse> | any) => {
          const data = e.response?.data;
          // const statusCode = e.response?.status;
          reject(data);
          options.onError?.({ status: e.response?.status, ...data });
          setLoading(false);
          try {
            console.error("[" + url + "] Api request error:", JSON.stringify(e, null, 2));
          } catch (error) {
            // TODO: check why this is happening
          }
          // TODO: enable again when everything is more stable
          // if (!options.disableToastErrorOn?.includes(statusCode)) {
          //   printErrorToast(e?.response?.data ?? e);
          // }
        })
        .finally(() => {
          setCalled(true);
          setLoading(false);
        });
    });
    return asyncCall;
  };
  /**
   *
   * @param error
   * @returns
   */
  // const isErrorResponse = (error: any): error is ErrorResponse => {
  //   return error && typeof error === 'object' && 'status' in error && 'error' in error;
  // };

  // const buildErrorToastContent = (errorMessage: string, correlationId: string) => {
  //   return React.createElement(
  //     'div',
  //     { className: 'toast-error-content' },
  //     React.createElement('div', { className: 'toast-error-message' }, errorMessage),
  //     React.createElement(
  //       'div',
  //       { className: 'toast-error-correlation-id' },
  //       correlationId ? 'Correlation ID: ' + correlationId : ''
  //     )
  //   );
  // };

  // const printErrorToast = (error: ApiError | ErrorResponse | any | undefined) => {
  //   let message = error ? error.message : 'Unexpected error';
  //   let correlationId = '';

  //   if (isErrorResponse(error)) {
  //     correlationId = error.correlationId;
  //     if (error.status == '500') {
  //       message = error.error;
  //     }
  //   }
  //   toast({
  //     message: buildErrorToastContent(message, correlationId),
  //     kind: 'error',
  //     timer: false
  //   });
  // };
  /**
   * Output
   */
  return { called, loading, call };
}
